import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { STATUS } from "@/lib/orders";
import { layoutDocument } from "@/lib/docLayout";
import TemplateDoc from "@/components/print/TemplateDoc";
import InvoiceShare from "@/components/orders/InvoiceShare";

const recorded = new Set();

// /siparisler/:id/yazdir/t/:tid (tid: şablon id | default:<tür>). ?kayit=1 → baskı burada sayılır (faturalaştır → otomatik baskı).
export default function TemplatePrint({ tidOverride }) {
  const { id, tid: tidParam } = useParams();
  const tid = tidOverride || tidParam;
  const navigate = useNavigate();
  const loc = useLocation();
  const [sp, setSp] = useSearchParams();
  const started = useRef(false);
  const [st, setSt] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    Promise.all([api.get(`/orders/${id}`), api.get(`/orders/${id}/invoice-meta`).catch(() => ({ data: { company: null, customer: {} } })), api.get(`/orders/${id}/print-template/${tid}`)])
      .then(([o, m, t]) => setSt({ o: o.data, meta: m.data, t: t.data })).catch((e) => setErr(apiError(e)));
  }, [id, tid]);
  useEffect(() => {
    if (!st || started.current) return;
    started.current = true;
    (async () => {
      if (sp.get("kayit") === "1" && !recorded.has(loc.key)) {
        recorded.add(loc.key);
        try { const { data } = await api.post(`/orders/${id}/print`, { doc: st.t.doc_type, template_id: st.t.id }); setSt((p) => ({ ...p, o: { ...p.o, ...data } })); } catch { /* baskı yine açılır */ }
        setSp({}, { replace: true });
      }
      await document.fonts.ready;
      setTimeout(() => window.print(), 300);
    })();
  }, [st]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <div className="p-8 text-rose-600" data-testid="template-print-error">{err}</div>;
  if (!st) return <div className="p-8 text-slate-400">Yükleniyor…</div>;
  const layout = layoutDocument(st.t, { o: st.o, meta: st.meta, now: formatDate(new Date().toISOString()), statusLabel: STATUS[st.o.status]?.[0], docTitle: st.t.doc_type_name });
  return (
    <div className="bg-slate-100 min-h-screen print:bg-white" data-testid="order-print-page">
      <style>{`@page { size: ${st.t.page.size} ${st.t.page.orientation}; margin: 0 } @media print { .tpl-page { page-break-after: always; box-shadow: none !important; margin: 0 !important } }`}</style>
      <div className="max-w-[260mm] mx-auto py-4 px-2 print:hidden flex flex-wrap gap-2 justify-between items-center">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/siparisler/${id}`)} className="h-9 px-3 rounded border border-slate-300 bg-white text-sm flex items-center gap-1 hover:bg-slate-50" data-testid="print-back-to-order-button"><ArrowLeft className="h-4 w-4" />Siparişe Dön</button>
          <span className="text-sm text-slate-600" data-testid="print-template-name">{st.t.doc_type_name} · {st.t.name} · {st.o.order_no} · {layout.pages.length} sayfa</span>
        </div>
        <div className="flex items-center gap-2">
          <InvoiceShare id={id} defaultTemplate={st.t.id} />
          <button onClick={() => window.print()} className="h-9 px-3 rounded bg-slate-900 text-white text-sm flex items-center gap-1" data-testid="print-again-button"><Printer className="h-4 w-4" />Yazdır / PDF</button>
        </div>
      </div>
      <div className="mx-auto w-fit space-y-4 print:space-y-0 [&_.tpl-page]:shadow" data-testid={st.t.doc_type === "proforma" ? "proforma-doc" : st.t.doc_type === "fatura" ? "invoice-doc" : "template-print-doc"}>
        <TemplateDoc layout={layout} logo={st.meta.company?.logo_url} />
      </div>
    </div>
  );
}
